import {
  Button,
  Flex,
  HStack,
  IconButton,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import {
  EntityId,
  QuantitySelectorWithSuggestions,
  QuantitySuggestionItem,
} from "@jackfruit/common"
import { clamp } from "lodash"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { FiMinus, FiPlus } from "react-icons/fi"
import { useCart } from "~/hooks/useCart"
import { useLineItem, useLineItemActions } from "~/hooks/useLineItem"
import { useProcessActions } from "~/hooks/useProcessActions"
import { interactionResponse } from "~/services/webvitals/defer"
import { getQuantitySelectorConfig } from "./helpers"

interface Props {
  lineItemId: EntityId
  lineItemQuantityLimit: number
}

const LineItemPreviewQuantitySelectorWithSuggestions: React.FC<Props> = ({
  lineItemId,
  lineItemQuantityLimit,
}) => {
  const { t } = useTranslation()

  const { cart } = useCart()
  const { fulfillment } = cart

  const { lineItem, product } = useLineItem(lineItemId)
  const { updateMetaData } = useLineItemActions()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const process = useProcessActions()
  const { updateQuantity } = useLineItemActions()

  const handleClickOnChangeQuantity = useCallback(() => {
    const previousMetaData = lineItem.metaData || {}
    updateMetaData(lineItemId, {
      ...previousMetaData,
      changeQuantityButtonClicked: true,
    })
    onOpen()
  }, [lineItem.metaData, lineItemId, onOpen, updateMetaData])

  const handleQuantityChange = useCallback(
    async (newQuantity: number) => {
      await interactionResponse()
      const clampedQuantity = clamp(newQuantity, 1, lineItemQuantityLimit)
      const goUp = clampedQuantity > lineItem.quantity

      if (lineItem.quantity !== newQuantity) {
        process.fireAnalyticEvent({
          eventType: goUp ? "addOneItemToCart" : "removeOneItemFromCart",
          data: { lineItemId },
        })
      }

      updateQuantity(lineItemId, clampedQuantity)
    },
    [
      lineItem.quantity,
      lineItemId,
      lineItemQuantityLimit,
      process,
      updateQuantity,
    ]
  )

  const handleQuantityDec = useCallback(
    () => handleQuantityChange(lineItem.quantity - 1),
    [handleQuantityChange, lineItem.quantity]
  )

  const handleQuantityInc = useCallback(
    () => handleQuantityChange(lineItem.quantity + 1),
    [handleQuantityChange, lineItem.quantity]
  )

  const handleSetQuantityPack = useCallback(
    (pack: QuantitySuggestionItem) => {
      const previousMetaData = lineItem.metaData || {}
      updateMetaData(lineItemId, {
        ...previousMetaData,
        quantityPackSelected: true,
        quantityPackSelectedName: pack.label,
        quantityPackSelectedValue: pack.quantity,
      })
      handleQuantityChange(pack.quantity)
    },
    [handleQuantityChange, lineItem.metaData, lineItemId, updateMetaData]
  )

  const modalConfiguration = getQuantitySelectorConfig({
    product,
    fulfillment,
  }) as QuantitySelectorWithSuggestions

  const items = modalConfiguration.items ?? []

  return (
    <>
      <Flex
        pl={3.5}
        pr={2}
        alignItems="center"
        justifyContent="space-between"
        flex={1}
      >
        <HStack spacing={0}>
          <IconButton
            size="sm"
            rounded="full"
            aria-label={t("components.cart.LineItemPreview.remove")}
            icon={<FiMinus />}
            data-testid={`lineItemPreviewRemoveOneBtn-${lineItem.productId}`}
            className="decLineItemQuantity"
            onClick={handleQuantityDec}
          />
          <NumberInput
            min={1}
            max={lineItemQuantityLimit}
            step={1}
            width="3rem"
            paddingX={0}
            borderWidth={0}
            onFocus={event => event.target.select()}
            value={lineItem.quantity}
            onChange={value => {
              handleQuantityChange(parseInt(value) || 0)
            }}
          >
            <NumberInputField textAlign="center" borderWidth={0} padding={0} />
          </NumberInput>
          <IconButton
            size="sm"
            rounded="full"
            data-testid={`lineItemPreviewAddoneBtn-${lineItem.productId}`}
            aria-label={t("components.cart.LineItemPreview.add")}
            icon={<FiPlus />}
            className="incLineItemQuantity"
            onClick={handleQuantityInc}
            disabled={lineItem.quantity >= lineItemQuantityLimit}
          />
        </HStack>
        <Button
          h={10}
          size="sm"
          _hover={{ textDecoration: "none" }}
          rounded="full"
          variant="outline"
          fontWeight="normal"
          onClick={handleClickOnChangeQuantity}
          className="changeItemQuantity"
        >
          {t(
            "components.cart.line-item.quantity-selector.LineItemPreviewQuantitySelectorWithSuggestions.changeQuantityButton"
          )}
        </Button>
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        autoFocus={false}
        returnFocusOnClose={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(
              "components.cart.line-item.quantity-selector.LineItemPreviewQuantitySelectorWithSuggestions.modal.title"
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InputGroup alignItems="center" gap={3} mb={6}>
              <IconButton
                size="sm"
                rounded="full"
                aria-label={t("components.cart.LineItemPreview.remove")}
                icon={<FiMinus />}
                data-testid={`lineItemPreviewRemoveOneBtn-${lineItem.productId}`}
                className="decLineItemQuantity"
                onClick={handleQuantityDec}
              />
              <NumberInput
                w="full"
                min={1}
                max={lineItemQuantityLimit}
                step={1}
                onFocus={event => event.target.select()}
                value={lineItem.quantity}
                onChange={value => {
                  handleQuantityChange(parseInt(value) || 0)
                }}
              >
                <NumberInputField textAlign="center" />
              </NumberInput>
              <IconButton
                size="sm"
                rounded="full"
                data-testid={`lineItemPreviewAddoneBtn-${lineItem.productId}`}
                aria-label={t("components.cart.LineItemPreview.add")}
                icon={<FiPlus />}
                className="incLineItemQuantity"
                onClick={handleQuantityInc}
                disabled={lineItem.quantity >= lineItemQuantityLimit}
              />
            </InputGroup>
            <Text fontWeight="bold" mb={4}>
              {t(
                "components.cart.line-item.quantity-selector.LineItemPreviewQuantitySelectorWithSuggestions.modal.optionsTitle"
              )}
            </Text>
            <Stack spacing={4}>
              {items.map(item => {
                const isSelected = item.quantity === lineItem.quantity
                return (
                  <Button
                    key={item.quantity}
                    onClick={() => {
                      handleSetQuantityPack(item)

                      setTimeout(() => {
                        onClose()
                      }, 100)
                    }}
                    color={isSelected ? "white" : "gray.500"}
                    className={`setItemQuantity-${item.quantity}`}
                    colorScheme={isSelected ? "primary" : "gray"}
                  >
                    {item.label} {item.quantity}
                  </Button>
                )
              })}
            </Stack>
          </ModalBody>
          <ModalFooter px={3}>
            <Button
              colorScheme="primary"
              mr={3}
              className="doneChangingItemQuantity"
              onClick={onClose}
            >
              {t(
                "components.cart.line-item.quantity-selector.LineItemPreviewQuantitySelectorWithSuggestions.modal.closeButton"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default LineItemPreviewQuantitySelectorWithSuggestions
